import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type AcademicYear={
  academicYearId:string;
  yearCode:string;
  startsOn:string;
  endsOn:string;
  status:string;
};

type SessionRow={
  sessionId:string;
  startsAt:string;
  endsAt:string;
  locationName?:string|null;
  capacity?:number|null;
  status:string;
  registeredCount:number;
  attendedCount:number;
  completedCount:number;
};

type ActivityRow={
  activityId:string;
  code?:string|null;
  title:string;
  activityType:string;
  deliveryMode:string;
  description?:string|null;
  status:string;
  evidenceExpected:boolean;
  required:boolean;
  version:number;
  sessions:SessionRow[];
};

type ProgramRow={
  programId:string;
  code?:string|null;
  title:string;
  programType:string;
  description?:string|null;
  status:string;
  targetGradeMin?:number|null;
  targetGradeMax?:number|null;
  capacity?:number|null;
  startsOn?:string|null;
  endsOn?:string|null;
  version:number;
  participantCount:number;
  completedLearnerCount:number;
  activities:ActivityRow[];
};

type PlanRow={
  planId:string;
  academicYearId:string;
  yearCode:string;
  title:string;
  description?:string|null;
  status:string;
  validFrom?:string|null;
  validUntil?:string|null;
  version:number;
  programs:ProgramRow[];
};

type LearnerOption={
  learnerId:string;
  displayName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
};

type Workspace={
  academicYears:AcademicYear[];
  plans:PlanRow[];
  learners:LearnerOption[];
  canApprovePlan:boolean;
};

type Participant={
  participationId:string;
  learnerId:string;
  displayName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
  sessionId?:string|null;
  status:string;
  version:number;
  registeredAt?:string|null;
  attendedAt?:string|null;
  completedAt?:string|null;
  evidence:Array<{
    evidenceId:string;
    title:string;
    status:string;
    type:string;
  }>;
};

function statusLabel(value:string){
  const map:Record<string,string>={
    DRAFT:"초안",
    APPROVED:"승인",
    ACTIVE:"활성",
    SCHEDULED:"일정화",
    OPEN:"운영 중",
    CLOSED:"종료",
    CANCELLED:"취소",
    REGISTERED:"등록",
    ATTENDED:"출석",
    COMPLETED:"완료",
    ABSENT:"결석",
    WITHDRAWN:"철회"
  };
  return map[value] || value;
}

function formatDate(value?:string|null){
  if(!value) return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",
    month:"2-digit",
    day:"2-digit",
    hour:"2-digit",
    minute:"2-digit"
  });
}

function toIso(value:string){
  if(!value) return "";
  return new Date(value).toISOString();
}

export function CenterProgramsPage({
  client,
  schoolId,
  initialSelection,
  onBusyChange
}:{
  client:PlatformClient;
  schoolId:string;
  initialSelection?:{planId:string;programId:string};
  onBusyChange?:(busy:boolean)=>void;
}){
  const [workspace,setWorkspace]=useState<Workspace|null>(null);
  const [participants,setParticipants]=useState<Participant[]>([]);
  const [selectedPlanId,setSelectedPlanId]=useState(initialSelection?.planId || "");
  const [selectedProgramId,setSelectedProgramId]=useState(initialSelection?.programId || "");
  const [selectedActivityId,setSelectedActivityId]=useState("");
  const [selectedSessionId,setSelectedSessionId]=useState("");
  const [selectedLearners,setSelectedLearners]=useState<string[]>([]);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);
  // CENTER-PLAN-OPERATIONS-SELECTION-04: same component, existing server authorization.
  useEffect(()=>{onBusyChange?.(busy!=="");},[busy,onBusyChange]);

  const [planYearId,setPlanYearId]=useState("");
  const [planTitle,setPlanTitle]=useState("");
  const [planDescription,setPlanDescription]=useState("");

  const [programCode,setProgramCode]=useState("");
  const [programTitle,setProgramTitle]=useState("");
  const [programType,setProgramType]=useState("PROJECT");
  const [programDescription,setProgramDescription]=useState("");
  const [gradeMin,setGradeMin]=useState("7");
  const [gradeMax,setGradeMax]=useState("9");
  const [programCapacity,setProgramCapacity]=useState("30");
  const [programStart,setProgramStart]=useState("");
  const [programEnd,setProgramEnd]=useState("");

  const [activityCode,setActivityCode]=useState("");
  const [activityTitle,setActivityTitle]=useState("");
  const [activityType,setActivityType]=useState("EXPERIENCE");
  const [deliveryMode,setDeliveryMode]=useState("OFFLINE");
  const [activityDescription,setActivityDescription]=useState("");
  const [evidenceExpected,setEvidenceExpected]=useState(true);
  const [required,setRequired]=useState(true);

  const [sessionStart,setSessionStart]=useState("");
  const [sessionEnd,setSessionEnd]=useState("");
  const [sessionLocation,setSessionLocation]=useState("");
  const [sessionCapacity,setSessionCapacity]=useState("30");

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_program_workspace",{p_school_id:schoolId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workspace;
      if(initialSelection){
        const requestedPlan=next.plans.find(p=>p.planId===initialSelection.planId);
        if(!requestedPlan?.programs.some(p=>p.programId===initialSelection.programId)){
          setWorkspace(null);setSelectedPlanId("");setSelectedProgramId("");setSelectedActivityId("");setParticipants([]);
          setError("선택한 활동을 이 학교의 운영 목록에서 찾지 못했어요. 계획으로 돌아가 확인해 주세요.");
          return;
        }
      }
      setWorkspace(next);

      setPlanYearId((current)=>
        next.academicYears.some((item)=>item.academicYearId===current)
          ? current
          : next.academicYears.find((item)=>item.status==="ACTIVE")?.academicYearId
            || next.academicYears[0]?.academicYearId
            || ""
      );

      setSelectedPlanId((current)=>
        next.plans.some((item)=>item.planId===current)
          ? current
          : next.plans.find((item)=>item.status==="ACTIVE")?.planId
            || next.plans[0]?.planId
            || ""
      );
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  const selectedPlan=useMemo(
    ()=>workspace?.plans.find((item)=>item.planId===selectedPlanId) || null,
    [workspace,selectedPlanId]
  );

  useEffect(()=>{
    if(!selectedPlan){
      if(workspace) setSelectedProgramId("");
      return;
    }
    setSelectedProgramId((current)=>
      selectedPlan.programs.some((item)=>item.programId===current)
        ? current
        : selectedPlan.programs[0]?.programId || ""
    );

    if(!programStart) setProgramStart(selectedPlan.validFrom || "");
    if(!programEnd) setProgramEnd(selectedPlan.validUntil || "");
  },[selectedPlan]);

  const selectedProgram=useMemo(
    ()=>selectedPlan?.programs.find((item)=>item.programId===selectedProgramId) || null,
    [selectedPlan,selectedProgramId]
  );

  useEffect(()=>{
    if(!selectedProgram){
      setSelectedActivityId("");
      return;
    }
    setSelectedActivityId((current)=>
      selectedProgram.activities.some((item)=>item.activityId===current)
        ? current
        : selectedProgram.activities[0]?.activityId || ""
    );
  },[selectedProgram]);

  const selectedActivity=useMemo(
    ()=>selectedProgram?.activities.find((item)=>item.activityId===selectedActivityId) || null,
    [selectedProgram,selectedActivityId]
  );

  useEffect(()=>{
    if(!selectedActivity){
      setSelectedSessionId("");
      setParticipants([]);
      return;
    }

    setSelectedSessionId((current)=>
      selectedActivity.sessions.some((item)=>item.sessionId===current)
        ? current
        : selectedActivity.sessions.find((item)=>item.status==="OPEN")?.sessionId
          || selectedActivity.sessions[0]?.sessionId
          || ""
    );
  },[selectedActivity]);

  useEffect(()=>{
    if(!selectedActivityId){
      setParticipants([]);
      return;
    }
    let active=true;
    client.rpc("center_program_participants",{
      p_school_id:schoolId,
      p_activity_id:selectedActivityId,
      p_session_id:null
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setParticipants((data || []) as Participant[]);
    });
    return()=>{active=false;};
  },[client,schoolId,selectedActivityId,refresh]);

  async function run(
    key:string,
    action:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>
  ){
    setBusy(key);
    setError("");
    try{
      const result=await action();
      if(result.error) throw new Error(result.error.message);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
      return null;
    }finally{
      setBusy("");
    }
  }

  async function registerSelected(){
    if(!selectedActivityId || selectedLearners.length===0) return;
    setBusy("register");
    setError("");

    try{
      for(const learnerId of selectedLearners){
        const {error}=await client.rpc("center_register_participation",{
          p_school_id:schoolId,
          p_learner_id:learnerId,
          p_activity_id:selectedActivityId,
          p_session_id:selectedSessionId || null
        });
        if(error) throw new Error(error.message);
      }
      setSelectedLearners([]);
      setRefresh((value)=>value+1);
    }catch(err){
      setError(err instanceof Error ? err.message : "참가자 등록 중 오류가 발생했습니다.");
    }finally{
      setBusy("");
    }
  }

  function toggleLearner(id:string){
    setSelectedLearners((current)=>
      current.includes(id)
        ? current.filter((item)=>item!==id)
        : [...current,id]
    );
  }

  const activeLearnerIds=new Set(
    participants
      .filter((item)=>["REGISTERED","ATTENDED","COMPLETED"].includes(item.status))
      .map((item)=>item.learnerId)
  );

  const availableLearners=(workspace?.learners || []).filter((learner)=>{
    if(activeLearnerIds.has(learner.learnerId)) return false;
    const grade=Number(learner.gradeLevel);
    if(Number.isFinite(grade) && selectedProgram){
      if(selectedProgram.targetGradeMin!=null && grade<selectedProgram.targetGradeMin) return false;
      if(selectedProgram.targetGradeMax!=null && grade>selectedProgram.targetGradeMax) return false;
    }
    return true;
  });

  return (
    <section className="program-ops-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">수업·활동</p>
          <h2>수업을 고르고 학생 참여를 운영하세요</h2>
          <p className="route-description">
            평소에는 운영 중인 수업·체험만 사용하고, 새 계획이나 프로그램이 필요할 때만 설정을 펼치면 됩니다.
          </p>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      <details className="teacher-setup-details">
        <summary>
          <span><strong>새 계획·프로그램 만들기</strong><small>학기 초 설정이나 새 프로그램 개설 때만 사용</small></span>
          <b>설정 열기</b>
        </summary>
        <div className="program-create-grid">
        <section className="program-create-card">
          <h3>교육계획</h3>
          <div className="program-form">
            <label>
              학년도
              <select value={planYearId} onChange={(event)=>setPlanYearId(event.target.value)}>
                {(workspace?.academicYears || []).map((year)=>(
                  <option key={year.academicYearId} value={year.academicYearId}>
                    {year.yearCode} · {statusLabel(year.status)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              계획명
              <input value={planTitle} onChange={(event)=>setPlanTitle(event.target.value)} placeholder="2026 진로교육 운영계획" />
            </label>
            <label className="wide">
              설명
              <input value={planDescription} onChange={(event)=>setPlanDescription(event.target.value)} />
            </label>
            <button
              className="secondary"
              disabled={busy!=="" || !planYearId || !planTitle.trim()}
              onClick={async()=>{
                const id=await run("plan-create",()=>client.rpc("center_create_career_plan",{
                  p_school_id:schoolId,
                  p_academic_year_id:planYearId,
                  p_title:planTitle,
                  p_description:planDescription || null
                }));
                if(id) setSelectedPlanId(String(id));
              }}
            >
              계획 생성
            </button>
          </div>
        </section>

        <section className="program-create-card">
          <h3>프로그램</h3>
          <div className="program-form">
            <label>
              교육계획
              <select value={selectedPlanId} onChange={(event)=>setSelectedPlanId(event.target.value)}>
                {(workspace?.plans || []).map((plan)=>(
                  <option key={plan.planId} value={plan.planId}>
                    {plan.title} · {statusLabel(plan.status)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              코드
              <input value={programCode} onChange={(event)=>setProgramCode(event.target.value)} />
            </label>
            <label className="wide">
              프로그램명
              <input value={programTitle} onChange={(event)=>setProgramTitle(event.target.value)} />
            </label>
            <label>
              유형
              <select value={programType} onChange={(event)=>setProgramType(event.target.value)}>
                <option value="CLASS">수업</option>
                <option value="COUNSELING">상담</option>
                <option value="EXPERIENCE">체험</option>
                <option value="MENTORING">멘토링</option>
                <option value="PROJECT">프로젝트</option>
                <option value="CLUB">동아리</option>
                <option value="EVENT">행사</option>
                <option value="OTHER">기타</option>
              </select>
            </label>
            <label>
              정원
              <input type="number" value={programCapacity} onChange={(event)=>setProgramCapacity(event.target.value)} />
            </label>
            <label>
              최소 학년
              <input type="number" min="1" max="12" value={gradeMin} onChange={(event)=>setGradeMin(event.target.value)} />
            </label>
            <label>
              최대 학년
              <input type="number" min="1" max="12" value={gradeMax} onChange={(event)=>setGradeMax(event.target.value)} />
            </label>
            <label>
              시작일
              <input type="date" value={programStart} onChange={(event)=>setProgramStart(event.target.value)} />
            </label>
            <label>
              종료일
              <input type="date" value={programEnd} onChange={(event)=>setProgramEnd(event.target.value)} />
            </label>
            <label className="wide">
              설명
              <input value={programDescription} onChange={(event)=>setProgramDescription(event.target.value)} />
            </label>
            <button
              className="primary compact-button"
              disabled={busy!=="" || !selectedPlanId || !programTitle.trim()}
              onClick={async()=>{
                const id=await run("program-create",()=>client.rpc("center_create_program",{
                  p_school_id:schoolId,
                  p_plan_id:selectedPlanId,
                  p_code:programCode || null,
                  p_title:programTitle,
                  p_program_type:programType,
                  p_description:programDescription || null,
                  p_target_grade_min:gradeMin ? Number(gradeMin) : null,
                  p_target_grade_max:gradeMax ? Number(gradeMax) : null,
                  p_capacity:programCapacity ? Number(programCapacity) : null,
                  p_starts_on:programStart || null,
                  p_ends_on:programEnd || null
                }));
                if(id) setSelectedProgramId(String(id));
              }}
            >
              프로그램 생성
            </button>
          </div>
        </section>
        </div>
      </details>

      <div className="program-workspace">
        <aside className="program-tree-panel">
          <div className="panel-title">
            <strong>수업·활동 목록</strong>
            <span>운영할 항목을 선택하세요</span>
          </div>

          {(workspace?.plans || []).map((plan)=>(
            <div className="program-plan-node" key={plan.planId}>
              <button
                className={selectedPlanId===plan.planId ? "program-tree-row active" : "program-tree-row"}
                onClick={()=>setSelectedPlanId(plan.planId)}
              >
                <strong>{plan.title}</strong>
                <small>{plan.yearCode} · {statusLabel(plan.status)}</small>
              </button>

              {selectedPlanId===plan.planId && plan.programs.map((program)=>(
                <button
                  key={program.programId}
                  className={selectedProgramId===program.programId ? "program-tree-row child active" : "program-tree-row child"}
                  onClick={()=>setSelectedProgramId(program.programId)}
                >
                  <strong>{program.title}</strong>
                  <small>{statusLabel(program.status)} · {program.participantCount}명</small>
                </button>
              ))}
            </div>
          ))}
        </aside>

        <div className="program-detail-panel">
          {!selectedPlan ? (
            <p className="case-empty">교육계획을 생성하거나 선택하세요.</p>
          ) : (
            <>
              <header className="program-detail-head">
                <div>
                  <p className="eyebrow">{selectedPlan.yearCode} Career Plan</p>
                  <h2>{selectedPlan.title}</h2>
                  <p>{selectedPlan.validFrom || "-"} ~ {selectedPlan.validUntil || "-"}</p>
                </div>
                <div className="program-status-actions">
                  <span className={selectedPlan.status==="ACTIVE" ? "status-pill good" : "status-pill"}>
                    {statusLabel(selectedPlan.status)}
                  </span>
                  {workspace?.canApprovePlan && selectedPlan.status!=="ACTIVE" && (
                    <button
                      className="secondary"
                      disabled={busy!==""}
                      onClick={()=>run("plan-activate",()=>client.rpc("center_activate_career_plan",{
                        p_school_id:schoolId,
                        p_plan_id:selectedPlan.planId,
                        p_expected_version:selectedPlan.version,
                        p_reason:"Center에서 교육계획 활성화"
                      }))}
                    >
                      계획 승인·활성
                    </button>
                  )}
                </div>
              </header>

              {selectedProgram ? (
                <>
                  <div className="program-metric-grid">
                    <Metric label="참가 학생" value={selectedProgram.participantCount} />
                    <Metric label="완료 학생" value={selectedProgram.completedLearnerCount} />
                    <Metric label="활동" value={selectedProgram.activities.length} />
                    <Metric label="정원" value={selectedProgram.capacity ?? "-"} />
                  </div>

                  <section className="program-entity-card">
                    <div className="program-entity-head">
                      <div>
                        <p className="eyebrow">{selectedProgram.code || selectedProgram.programType}</p>
                        <h3>{selectedProgram.title}</h3>
                        <p>
                          {selectedProgram.targetGradeMin || "-"}~{selectedProgram.targetGradeMax || "-"}학년
                          {" · "}
                          {selectedProgram.startsOn || "-"} ~ {selectedProgram.endsOn || "-"}
                        </p>
                      </div>
                      <div className="program-status-actions">
                        <span className={selectedProgram.status==="OPEN" ? "status-pill good" : "status-pill"}>
                          {statusLabel(selectedProgram.status)}
                        </span>
                        {selectedProgram.status==="DRAFT" && (
                          <button className="secondary" disabled={busy!==""} onClick={()=>run("program-schedule",()=>client.rpc("center_transition_program",{
                            p_school_id:schoolId,
                            p_program_id:selectedProgram.programId,
                            p_expected_version:selectedProgram.version,
                            p_to_status:"SCHEDULED",
                            p_reason:"Center에서 일정화"
                          }))}>일정화</button>
                        )}
                        {selectedProgram.status==="SCHEDULED" && (
                          <button className="primary compact-button" disabled={busy!==""} onClick={()=>run("program-open",()=>client.rpc("center_transition_program",{
                            p_school_id:schoolId,
                            p_program_id:selectedProgram.programId,
                            p_expected_version:selectedProgram.version,
                            p_to_status:"OPEN",
                            p_reason:"Center에서 운영 시작"
                          }))}>프로그램 시작</button>
                        )}
                        {selectedProgram.status==="OPEN" && (
                          <button className="secondary" disabled={busy!==""} onClick={()=>run("program-close",()=>client.rpc("center_transition_program",{
                            p_school_id:schoolId,
                            p_program_id:selectedProgram.programId,
                            p_expected_version:selectedProgram.version,
                            p_to_status:"CLOSED",
                            p_reason:"Center에서 운영 종료"
                          }))}>프로그램 종료</button>
                        )}
                      </div>
                    </div>
                  </section>

                  <div className="program-operation-grid">
                    <section className="program-entity-card">
                      <h3>활동 설계</h3>
                      <div className="program-form">
                        <label>코드<input value={activityCode} onChange={(event)=>setActivityCode(event.target.value)} /></label>
                        <label>활동명<input value={activityTitle} onChange={(event)=>setActivityTitle(event.target.value)} /></label>
                        <label>
                          유형
                          <select value={activityType} onChange={(event)=>setActivityType(event.target.value)}>
                            <option value="LESSON">수업</option>
                            <option value="ASSESSMENT">검사</option>
                            <option value="COUNSELING">상담</option>
                            <option value="EXPERIENCE">체험</option>
                            <option value="PROJECT">프로젝트</option>
                            <option value="INTERVIEW">인터뷰</option>
                            <option value="REFLECTION">성찰</option>
                            <option value="PRESENTATION">발표</option>
                            <option value="OTHER">기타</option>
                          </select>
                        </label>
                        <label>
                          방식
                          <select value={deliveryMode} onChange={(event)=>setDeliveryMode(event.target.value)}>
                            <option value="OFFLINE">오프라인</option>
                            <option value="ONLINE">온라인</option>
                            <option value="HYBRID">혼합</option>
                            <option value="SELF_DIRECTED">자기주도</option>
                          </select>
                        </label>
                        <label className="wide">설명<input value={activityDescription} onChange={(event)=>setActivityDescription(event.target.value)} /></label>
                        <label className="check-label">
                          <input type="checkbox" checked={evidenceExpected} onChange={(event)=>setEvidenceExpected(event.target.checked)} />
                          완료 후 증빙 필요
                        </label>
                        <label className="check-label">
                          <input type="checkbox" checked={required} onChange={(event)=>setRequired(event.target.checked)} />
                          필수 활동
                        </label>
                        <button
                          className="primary compact-button"
                          disabled={busy!=="" || !activityTitle.trim() || !["DRAFT","SCHEDULED"].includes(selectedProgram.status)}
                          onClick={async()=>{
                            const id=await run("activity-create",()=>client.rpc("center_create_activity",{
                              p_school_id:schoolId,
                              p_program_id:selectedProgram.programId,
                              p_code:activityCode || null,
                              p_title:activityTitle,
                              p_activity_type:activityType,
                              p_delivery_mode:deliveryMode,
                              p_description:activityDescription || null,
                              p_evidence_expected:evidenceExpected,
                              p_required:required
                            }));
                            if(id) setSelectedActivityId(String(id));
                          }}
                        >
                          활동 추가
                        </button>
                      </div>
                    </section>

                    <section className="program-entity-card">
                      <h3>세션 일정</h3>
                      <div className="program-form">
                        <label>시작<input type="datetime-local" value={sessionStart} onChange={(event)=>setSessionStart(event.target.value)} /></label>
                        <label>종료<input type="datetime-local" value={sessionEnd} onChange={(event)=>setSessionEnd(event.target.value)} /></label>
                        <label>장소<input value={sessionLocation} onChange={(event)=>setSessionLocation(event.target.value)} /></label>
                        <label>정원<input type="number" value={sessionCapacity} onChange={(event)=>setSessionCapacity(event.target.value)} /></label>
                        <button
                          className="secondary"
                          disabled={busy!=="" || !selectedActivity || !sessionStart || !sessionEnd || !["SCHEDULED","OPEN"].includes(selectedActivity.status)}
                          onClick={async()=>{
                            const id=await run("session-create",()=>client.rpc("center_create_activity_session",{
                              p_school_id:schoolId,
                              p_activity_id:selectedActivityId,
                              p_starts_at:toIso(sessionStart),
                              p_ends_at:toIso(sessionEnd),
                              p_location_name:sessionLocation || null,
                              p_capacity:sessionCapacity ? Number(sessionCapacity) : null
                            }));
                            if(id) setSelectedSessionId(String(id));
                          }}
                        >
                          세션 추가
                        </button>
                      </div>
                    </section>
                  </div>

                  <section className="program-entity-card">
                    <div className="panel-title">
                      <strong>활동</strong>
                      <span>{selectedProgram.activities.length}개</span>
                    </div>

                    <div className="activity-tab-list">
                      {selectedProgram.activities.map((activity)=>(
                        <button
                          key={activity.activityId}
                          className={selectedActivityId===activity.activityId ? "activity-tab active" : "activity-tab"}
                          onClick={()=>setSelectedActivityId(activity.activityId)}
                        >
                          <strong>{activity.title}</strong>
                          <small>{statusLabel(activity.status)}{activity.evidenceExpected ? " · 증빙 필요" : ""}</small>
                        </button>
                      ))}
                    </div>

                    {selectedActivity && (
                      <>
                        <div className="activity-control-bar">
                          <div>
                            <strong>{selectedActivity.title}</strong>
                            <span>{selectedActivity.activityType} · {selectedActivity.deliveryMode}</span>
                          </div>
                          <div className="program-status-actions">
                            <span className={selectedActivity.status==="OPEN" ? "status-pill good" : "status-pill"}>
                              {statusLabel(selectedActivity.status)}
                            </span>
                            {selectedActivity.status==="DRAFT" && (
                              <button className="secondary" disabled={busy!==""} onClick={()=>run("activity-schedule",()=>client.rpc("center_transition_activity",{
                                p_school_id:schoolId,
                                p_activity_id:selectedActivity.activityId,
                                p_expected_version:selectedActivity.version,
                                p_to_status:"SCHEDULED",
                                p_reason:"Center에서 활동 일정화"
                              }))}>활동 일정화</button>
                            )}
                            {selectedActivity.status==="SCHEDULED" && selectedProgram.status==="OPEN" && (
                              <button className="primary compact-button" disabled={busy!==""} onClick={()=>run("activity-open",()=>client.rpc("center_transition_activity",{
                                p_school_id:schoolId,
                                p_activity_id:selectedActivity.activityId,
                                p_expected_version:selectedActivity.version,
                                p_to_status:"OPEN",
                                p_reason:"Center에서 활동 시작"
                              }))}>활동 시작</button>
                            )}
                            {selectedActivity.status==="OPEN" && (
                              <button className="secondary" disabled={busy!==""} onClick={()=>run("activity-close",()=>client.rpc("center_transition_activity",{
                                p_school_id:schoolId,
                                p_activity_id:selectedActivity.activityId,
                                p_expected_version:selectedActivity.version,
                                p_to_status:"CLOSED",
                                p_reason:"Center에서 활동 종료"
                              }))}>활동 종료</button>
                            )}
                          </div>
                        </div>

                        <div className="session-grid">
                          {selectedActivity.sessions.map((session)=>(
                            <article
                              key={session.sessionId}
                              className={selectedSessionId===session.sessionId ? "session-card active" : "session-card"}
                              onClick={()=>setSelectedSessionId(session.sessionId)}
                            >
                              <div className="session-card-head">
                                <strong>{formatDate(session.startsAt)}</strong>
                                <span className={session.status==="OPEN" ? "status-pill good" : "status-pill"}>
                                  {statusLabel(session.status)}
                                </span>
                              </div>
                              <p>{session.locationName || "장소 미정"}</p>
                              <small>
                                등록 {session.registeredCount} · 출석 {session.attendedCount} · 완료 {session.completedCount}
                                {session.capacity ? " · 정원 "+session.capacity : ""}
                              </small>
                              <div className="wallet-button-row">
                                {session.status==="SCHEDULED" && selectedActivity.status==="OPEN" && (
                                  <button
                                    className="secondary"
                                    disabled={busy!==""}
                                    onClick={(event)=>{
                                      event.stopPropagation();
                                      void run("session-open",()=>client.rpc("center_transition_activity_session",{
                                        p_school_id:schoolId,
                                        p_session_id:session.sessionId,
                                        p_to_status:"OPEN",
                                        p_reason:"Center에서 세션 시작"
                                      }));
                                    }}
                                  >
                                    세션 시작
                                  </button>
                                )}
                                {session.status==="OPEN" && (
                                  <button
                                    className="secondary"
                                    disabled={busy!==""}
                                    onClick={(event)=>{
                                      event.stopPropagation();
                                      void run("session-complete",()=>client.rpc("center_transition_activity_session",{
                                        p_school_id:schoolId,
                                        p_session_id:session.sessionId,
                                        p_to_status:"COMPLETED",
                                        p_reason:"Center에서 세션 종료"
                                      }));
                                    }}
                                  >
                                    세션 종료
                                  </button>
                                )}
                              </div>
                            </article>
                          ))}
                          {!selectedActivity.sessions.length && <p className="case-empty">등록된 세션이 없습니다.</p>}
                        </div>
                      </>
                    )}
                  </section>

                  {selectedActivity && (
                    <section className="program-entity-card">
                      <div className="panel-title">
                        <strong>참가·출석</strong>
                        <span>{participants.length}명</span>
                      </div>

                      <div className="participant-register-panel">
                        <div className="participant-session-select">
                          <label>
                            등록 세션
                            <select value={selectedSessionId} onChange={(event)=>setSelectedSessionId(event.target.value)}>
                              <option value="">세션 없음</option>
                              {selectedActivity.sessions
                                .filter((session)=>["SCHEDULED","OPEN"].includes(session.status))
                                .map((session)=>(
                                  <option key={session.sessionId} value={session.sessionId}>
                                    {formatDate(session.startsAt)} · {session.locationName || "장소 미정"}
                                  </option>
                                ))}
                            </select>
                          </label>
                        </div>

                        <div className="learner-check-grid">
                          {availableLearners.map((learner)=>(
                            <label key={learner.learnerId} className="learner-check">
                              <input
                                type="checkbox"
                                checked={selectedLearners.includes(learner.learnerId)}
                                onChange={()=>toggleLearner(learner.learnerId)}
                              />
                              <span>
                                <strong>{learner.displayName}</strong>
                                <small>
                                  {learner.gradeLevel ? learner.gradeLevel+"학년" : "학년 미정"}
                                  {learner.classLabel ? " · "+learner.classLabel+"반" : ""}
                                </small>
                              </span>
                            </label>
                          ))}
                          {!availableLearners.length && <p className="case-empty">추가 등록 가능한 학생이 없습니다.</p>}
                        </div>

                        <button
                          className="primary compact-button"
                          disabled={busy!=="" || selectedLearners.length===0}
                          onClick={registerSelected}
                        >
                          {busy==="register" ? "등록 중…" : selectedLearners.length+"명 참가 등록"}
                        </button>
                      </div>

                      <div className="participant-table">
                        {participants.map((participant)=>(
                          <div className="participant-row" key={participant.participationId}>
                            <div>
                              <strong>{participant.displayName}</strong>
                              <span>
                                {participant.gradeLevel ? participant.gradeLevel+"학년" : ""}
                                {participant.classLabel ? " · "+participant.classLabel+"반" : ""}
                              </span>
                              {participant.evidence.length>0 && (
                                <small>
                                  증빙 {participant.evidence.map((item)=>statusLabel(item.status)).join(", ")}
                                </small>
                              )}
                            </div>
                            <div className="participant-actions">
                              <span className={participant.status==="COMPLETED" ? "status-pill good" : "status-pill"}>
                                {statusLabel(participant.status)}
                              </span>
                              {participant.status==="REGISTERED" && (
                                <>
                                  <button className="secondary" disabled={busy!==""} onClick={()=>run("attendance",()=>client.rpc("center_transition_participation",{
                                    p_school_id:schoolId,
                                    p_participation_id:participant.participationId,
                                    p_expected_version:participant.version,
                                    p_to_status:"ATTENDED",
                                    p_reason:"Center 출석 처리"
                                  }))}>출석</button>
                                  <button className="secondary danger" disabled={busy!==""} onClick={()=>run("absence",()=>client.rpc("center_transition_participation",{
                                    p_school_id:schoolId,
                                    p_participation_id:participant.participationId,
                                    p_expected_version:participant.version,
                                    p_to_status:"ABSENT",
                                    p_reason:"Center 결석 처리"
                                  }))}>결석</button>
                                </>
                              )}
                              {participant.status==="ATTENDED" && (
                                <button className="primary compact-button" disabled={busy!==""} onClick={()=>run("complete-participation",()=>client.rpc("center_transition_participation",{
                                  p_school_id:schoolId,
                                  p_participation_id:participant.participationId,
                                  p_expected_version:participant.version,
                                  p_to_status:"COMPLETED",
                                  p_reason:"Center 활동 완료"
                                }))}>완료</button>
                              )}
                            </div>
                          </div>
                        ))}
                        {!participants.length && <p className="case-empty">등록된 참가자가 없습니다.</p>}
                      </div>
                    </section>
                  )}
                </>
              ) : (
                <p className="case-empty">프로그램을 생성하거나 선택하세요.</p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function Metric({label,value}:{label:string;value:number|string}){
  return <div className="program-metric-card"><span>{label}</span><strong>{value}</strong></div>;
}
